/**
 * The replacement pipeline — the interceptor half of SPEC.md §4.1 (CR §6.5).
 *
 * A **pending event** is something that is about to happen and has not happened yet. Between
 * an effect saying "deal 2 damage" and the counters landing, every replacement effect in play
 * gets a look, and any that applies **rewrites the event**. The replaced event then never
 * happens at all: nothing is emitted for it, so — since `GameEvent` is the substrate the bag
 * matches triggers against (`bag.ts`) — its would-have-been triggers never fire. What the
 * replacement produces *does* happen, and fires its own triggers.
 *
 * ```
 *   dealDamage(…)                       ← damage.ts, the taxonomy's entry points
 *     └ pending event { verb, target, amount }
 *         ├ damage modifiers (Resist)   ← before replacements, per the CR 2.2 clarification
 *         ├ nextReplacement  ─ rewriteEvent ─┐   this module
 *         │      ▲                           │
 *         │      └───────── each produced event re-enters (chaining)
 *         └ perform → emit GameEvent    ← damage.ts; only now do triggers see anything
 * ```
 *
 * ## What this module owns
 *
 * 1. **Registration** — there is none. Replacements are *continuous* abilities, read on
 *    demand from the cards in play the same way a static buff is (SPEC.md §4.5), so nothing
 *    lives in `GameState`, nothing clones, and nothing can go stale. `nextReplacement` is a
 *    scan in turn order from the active player, then play-zone order, then ability index —
 *    deterministic, which is what the AI and the replay log require.
 * 2. **Priority** — the affected card's **own** replacement applies first (CR §6.5.7); every
 *    other candidate follows in scan order. One replacement applies per pass, and the pass
 *    repeats on the resulting event until nothing applies.
 * 3. **Once per event** (CR §6.5.6) — a replacement that has applied is out for the rest of
 *    that event's lineage, including events derived from it. That is also what makes two
 *    cards redirecting damage at each other terminate instead of ping-ponging forever.
 * 4. **Same-replacement dedup** (CR §6.5.8) — two instances of the *same* replacement can't
 *    both apply to one event; the others cease to exist. The CR gives the affected player the
 *    choice of which one; identical replacements differ only in their source, so the engine
 *    resolves it deterministically in scan order rather than raising a `ChoiceRequest` for a
 *    decision with no observable stakes.
 *
 * `damage.ts` owns what a damage event *means*; this module owns who gets to change it.
 */
import type { CardRef, DamageVerb, EventAmount, ReplacementAbility } from './abilities/ir.ts';
import type { AbilityContext } from './abilities/selectors.ts';
import {
  baseContext,
  evalAmount,
  evalCondition,
  matchesSelector,
  resolveCardRef,
} from './abilities/selectors.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';
import type { AbilityModifier } from './modifiers.ts';
import {
  grantedAbilityEntriesOf,
  normaliseAbilityName,
  suppressedAbilityNames,
} from './modifiers.ts';
import type { GameState } from './state.ts';
import { instanceAt, playerAt, turnOrderFrom } from './state.ts';

// ---------------------------------------------------------------------------
// Pending events
// ---------------------------------------------------------------------------

/**
 * A damage event that has not happened yet.
 *
 * The same record covers all four verbs, because they are the same event with different rule
 * hooks: `target` is the card the event is *about* (the one that gains counters, or loses
 * them for `remove`), and `from` is the donor a `move` takes them off.
 */
export interface DamagePendingEvent {
  readonly kind: 'damage';
  readonly verb: DamageVerb;
  readonly target: InstanceId;
  /** Where a `move` takes its counters from; `null` for every other verb. */
  readonly from: InstanceId | null;
  readonly amount: number;
  /** The card whose effect is doing this, or `null` for damage with no card source. */
  readonly source: InstanceId | null;
  /** "This damage can't be reduced by Resist" — skips `applyDamageModifiers`'s Resist step. */
  readonly ignoresResist?: boolean;
}

/**
 * A lore loss that has not happened yet — "you **would** lose N lore". Player-scoped: the
 * affected party is `player`, not a card, so `subject: 'self'` and `filter` never match it.
 */
export interface LoseLorePendingEvent {
  readonly kind: 'lose-lore';
  /** Who would lose the lore. */
  readonly player: PlayerIndex;
  readonly amount: number;
  /** The card whose effect is doing this, or `null` for a loss with no card source. */
  readonly source: InstanceId | null;
}

/**
 * A lore gain that has not happened yet — "you **would** gain N lore". The mirror of
 * `LoseLorePendingEvent`, and player-scoped in exactly the same way; every source of a gain
 * raises it (`gainLoreEvent`, `damage.ts`), so questing, a location's ◊ and an effect's
 * "gain N lore" are one event kind here.
 */
export interface GainLorePendingEvent {
  readonly kind: 'gain-lore';
  /** Who would gain the lore. */
  readonly player: PlayerIndex;
  readonly amount: number;
  /** The card whose effect is doing this, or `null` — questing and a location's ◊ have none. */
  readonly source: InstanceId | null;
}

/**
 * A forced discard that has not happened yet — "an effect **would** cause you to discard".
 * Raised before anyone is asked which cards, so `amount` is how many would go, and a
 * replacement catches the whole discard or none of it. Costs are not effects and never come
 * through here (`interpret.ts`'s cost payment discards directly).
 */
export interface DiscardPendingEvent {
  readonly kind: 'discard';
  /** Whose hand would lose the cards. */
  readonly player: PlayerIndex;
  /** How many cards would be discarded (already clamped to the hand). */
  readonly amount: number;
  /** The card whose effect is doing this, or `null` when nothing in play is. */
  readonly source: InstanceId | null;
}

/**
 * A card entering an inkwell that has not happened yet — the ordinary once-per-turn ink action
 * (CR §4.2). Player-scoped like `GainLorePendingEvent`: the affected party is `player`, whose
 * inkwell it is entering, not the card itself — "cards enter **opponents'** inkwells exerted"
 * (Daisy Duck - Paranormal Investigator) reads `player` against the replacement's controller
 * exactly as `gain-lore`'s `controller`/`opponent` scoping does.
 *
 * Unlike the other player-scoped events, this one has no natural `amount` (it is always exactly
 * one card) — `runReplaceableEvent` skips the "nothing to do" check for it accordingly.
 */
export interface InkPendingEvent {
  readonly kind: 'ink';
  /** Whose inkwell the card is entering. */
  readonly player: PlayerIndex;
  /** The card entering the inkwell. */
  readonly card: InstanceId;
  readonly defId: number;
  /** Whether it enters exerted rather than ready (CR §4.2's default). */
  readonly exerted: boolean;
  /** The card whose effect is doing this, or `null` for the ordinary turn action. */
  readonly source: InstanceId | null;
}

/**
 * A character or location that has not been banished yet — "this character **would be**
 * banished" (CR §6.5, ticket 137). Card-scoped like `DamagePendingEvent`, minus the verbs: a
 * banish is one thing, not a taxonomy. Raised at `zones.ts`'s `banish()` chokepoint, which
 * already snapshots `hadCardsUnder` (ticket 68) and `underCards`/`effectiveStrength`/
 * `hadExertedCharacterHere` (ticket 104) — those snapshots, and the `card-banished` /
 * `banished-in-challenge` events they ride on, belong to the **unreplaced** banish only
 * (`banish()` itself, called from `performEvent`'s `banish` case): a replaced banish never
 * happens, so none of that runs, exactly as a replaced discard never emits `card-discarded`.
 */
export interface BanishPendingEvent {
  readonly kind: 'banish';
  /** The character or location that would be banished. */
  readonly target: InstanceId;
  /** The card whose effect is doing this, or `null` for a game-state-check or cost banish. */
  readonly source: InstanceId | null;
}

/**
 * A card entering an inkwell that has not happened yet, **not** through the ordinary turn
 * action — today, only a `banish` replacement's own `to-inkwell` template produces this (`put
 * them into your inkwell … instead`). Deliberately a different kind from `InkPendingEvent`:
 * reusing `ink` would let an `ink`-catching replacement (Daisy Duck - Paranormal Investigator)
 * reach in here, which is exactly what `putIntoInkwell` (`zones.ts`) already declines to allow
 * for every other effect-driven put — this event kind is not (yet) itself a
 * `ReplaceableEventKind`, so nothing catches it, matching that precedent. Card-scoped like
 * `InkPendingEvent`: `card`/`defId` name the card, `player` is whose inkwell it enters.
 */
export interface ToInkwellPendingEvent {
  readonly kind: 'to-inkwell';
  /** Whose inkwell the card enters. */
  readonly player: PlayerIndex;
  /** The card entering the inkwell. */
  readonly card: InstanceId;
  readonly defId: number;
  readonly exerted: boolean;
  /** Whether it enters facedown — knowledge is not granted (`putIntoInkwell`'s own reasoning). */
  readonly facedown: boolean;
  /** The card whose effect is doing this, or `null` for one with no card source. */
  readonly source: InstanceId | null;
}

/**
 * Something that is about to happen. Widening this union — a draw, a banish — is how the
 * next family of "instead" cards joins the pipeline; each new kind needs a
 * `ReplaceableEventKind`, a matcher clause below, and a module that performs it.
 */
export type PendingEvent =
  | DamagePendingEvent
  | LoseLorePendingEvent
  | GainLorePendingEvent
  | DiscardPendingEvent
  | InkPendingEvent
  | BanishPendingEvent
  | ToInkwellPendingEvent;

// ---------------------------------------------------------------------------
// Candidates
// ---------------------------------------------------------------------------

/** One replacement effect that applies to one pending event. */
export interface ReplacementMatch {
  /** `i<instance>:r<abilityIndex>` — the once-per-event key (CR §6.5.6). */
  readonly key: string;
  /** `d<defId>:r<abilityIndex>` — the same-replacement key (CR §6.5.8). */
  readonly dedupe: string;
  readonly source: InstanceId;
  /** Who controls the replacement — the "you" of its selectors and amounts. */
  readonly controller: PlayerIndex;
  readonly ability: ReplacementAbility;
  /** Whether this is the affected card's own replacement, which goes first (CR §6.5.7). */
  readonly selfReplacement: boolean;
  /**
   * The modifier record backing a granted replacement — `null` for an authored ability and for
   * one granted by a continuous static, neither of which has a record to cross off. What
   * `consumeIfOneShot` marks the moment a `consumesOnApply` replacement actually applies.
   */
  readonly sourceModifier: AbilityModifier | null;
}

/** One replacement ability a card has, with the key space slot that identifies it. */
export interface ReplacementSlot {
  readonly ability: ReplacementAbility;
  readonly index: number;
  /** True when a continuous effect gave this card the ability rather than printing it. */
  readonly granted: boolean;
  /** See `ReplacementMatch.sourceModifier`. */
  readonly sourceModifier: AbilityModifier | null;
}

/**
 * The replacement abilities on one card, with the index that identifies each of them.
 *
 * Two sources, in the order `abilitiesOf` merges them: the catalog's authored IR, then the
 * whole abilities a continuous effect has **given** this card (CR §6.4.3) — a resolved
 * `grant-ability` or another card's `grant-ability` static. The granted read is what makes
 * "chosen character gains 'this character takes no damage from challenges' this turn" work at
 * all; it goes through `grantedAbilities`, so the grant's own scope and duration decide when
 * it stops applying, exactly as for a granted trigger or keyword.
 *
 * The keyword merge `abilitiesOf` also does is skipped: no keyword packages a replacement, and
 * this runs for every card in play on every damage event. So the indices here are `abilitiesOf`
 * positions for authored abilities and continue past the authored list for granted ones, which
 * is all the pipeline's own key space needs. **A keyword that ever grants a replacement joins
 * here.**
 */
export function replacementsOf(state: GameState, id: InstanceId): ReplacementSlot[] {
  const instance = instanceAt(state, id);
  const authored = state.catalog.abilities?.(instance.defId);
  // Ticket 118: a `suppress-ability` static drops a matching replacement by name exactly as it
  // drops any other ability kind (`abilitiesOf`, `interpret.ts`) — read here too, since this
  // path never goes through `abilitiesOf` itself.
  const suppressed = suppressedAbilityNames(state, instance);
  const isSuppressed = (name: string | undefined): boolean =>
    name !== undefined && suppressed.includes(normaliseAbilityName(name));
  const found: ReplacementSlot[] = [];
  authored?.forEach((ability, index) => {
    if (ability.kind === 'replacement' && !isSuppressed(ability.name)) {
      found.push({ ability, index, granted: false, sourceModifier: null });
    }
  });
  const granted = grantedAbilityEntriesOf(state, instance);
  granted.forEach((entry, offset) => {
    if (entry.ability.kind === 'replacement' && !isSuppressed(entry.ability.name)) {
      found.push({
        ability: entry.ability,
        index: (authored?.length ?? 0) + offset,
        granted: true,
        sourceModifier: entry.modifier,
      });
    }
  });
  return found;
}

/**
 * The replacement that applies next to this event, or `null` when none does.
 *
 * `applied` holds the keys already spent on this event's lineage — both the per-instance keys
 * and the per-replacement dedup keys — and the caller extends it each time it takes one.
 */
export function nextReplacement(
  state: GameState,
  event: PendingEvent,
  applied: readonly string[],
): ReplacementMatch | null {
  const candidates: ReplacementMatch[] = [];
  for (const player of turnOrderFrom(state, state.activePlayer)) {
    for (const id of playerAt(state, player).play) {
      const card = instanceAt(state, id);
      for (const { ability, index, granted, sourceModifier } of replacementsOf(state, id)) {
        const match: ReplacementMatch = {
          key: `i${id}:r${index}`,
          // A granted replacement is not printed on the card, so two instances of the same
          // definition can hold two *different* granted abilities at the same slot — the
          // defId key would wrongly call them the same replacement. Key those per instance,
          // which for CR §6.5.8 purposes is the same as never colliding.
          dedupe: granted ? `i${id}:g${index}` : `d${card.defId}:r${index}`,
          source: id,
          controller: card.controller,
          ability,
          // Only a card-scoped event (damage, banish) has an affected card for a replacement
          // to be "own" to.
          selfReplacement:
            (event.kind === 'damage' || event.kind === 'banish') && id === event.target,
          sourceModifier,
        };
        if (applied.includes(match.key) || applied.includes(match.dedupe)) continue;
        if (!replacementApplies(state, match, event)) continue;
        candidates.push(match);
      }
    }
  }
  // CR §6.5.7: self-replacement effects apply first, then one other, repeating.
  return candidates.find((each) => each.selfReplacement) ?? candidates[0] ?? null;
}

/** Whether one replacement's condition is met by one pending event (CR §6.5.3). */
function replacementApplies(
  state: GameState,
  match: ReplacementMatch,
  event: PendingEvent,
): boolean {
  const when = match.ability.when;
  if (when.event !== event.kind) return false;

  const ctx = contextOf(match, event);
  if (event.kind === 'damage' || event.kind === 'banish') {
    // `verbs` only means anything for the damage taxonomy — a banish has no sub-kinds.
    if (event.kind === 'damage') {
      if (when.verbs !== undefined && !when.verbs.includes(event.verb)) return false;
    } else if (when.verbs !== undefined) {
      return false;
    }

    const affected = instanceAt(state, event.target);
    switch (when.subject) {
      case 'any':
        break;
      case 'self':
        if (event.target !== match.source) return false;
        break;
      case 'controller':
        if (affected.controller !== match.controller) return false;
        break;
      case 'opponent':
        if (affected.controller === match.controller) return false;
        break;
    }

    if (when.filter !== undefined && !matchesSelector(state, ctx, affected, when.filter)) {
      return false;
    }
  } else {
    // Player-scoped events: the affected party is a player. `self` names an affected card,
    // which does not exist here, and neither a card `filter` nor a damage `verbs` clause has
    // anything to match — `defineAbilities` rejects all three, and refusing them here keeps
    // hand-built IR honest too.
    if (when.verbs !== undefined) return false;
    switch (when.subject) {
      case 'any':
        break;
      case 'self':
        return false;
      case 'controller':
        if (event.player !== match.controller) return false;
        break;
      case 'opponent':
        if (event.player === match.controller) return false;
        break;
    }
    if (when.filter !== undefined) return false;
  }

  const window = when.window ?? 'any-turn';
  if (window === 'your-turn' && state.activePlayer !== match.controller) return false;
  if (window === 'opponent-turn' && state.activePlayer === match.controller) return false;

  if (when.limit === 'once-per-turn' && state.abilityUses.includes(replacementLimitKey(match))) {
    return false;
  }

  if (when.guard !== undefined && !evalCondition(state, ctx, when.guard)) return false;
  return true;
}

/**
 * The `GameState.abilityUses` key for a replacement's own `limit: 'once-per-turn'` — distinct
 * from `match.key`, the per-event dedup (CR §6.5.6): that one is thrown away with `applied` at
 * the end of one event's lineage, this one persists in `state.abilityUses` across events until
 * the next Ready step. Keyed by `match.key` (source instance + ability slot), which is already
 * unique per replacement instance.
 */
function replacementLimitKey(match: ReplacementMatch): string {
  return `repl-limit:${match.key}`;
}

/**
 * A replacement resolves against its own card as `self` and the caught event's affected card
 * as `subject` (`null` for a player-scoped event, which has none), with no bindings — a
 * replacement never asks a player anything, which is why it can run inside the event's own
 * step rather than on the resolution stack.
 */
function contextOf(match: ReplacementMatch, event: PendingEvent): AbilityContext {
  return baseContext(
    match.source,
    match.controller,
    event.kind === 'damage' || event.kind === 'banish' ? event.target : null,
  );
}

/**
 * Cross a `consumesOnApply` replacement off the moment it actually **applies** — not merely
 * matches — so it never offers itself again (`grantedAbilityEntriesOf`'s `isSpentReplacement`
 * check reads this). The caller (`damage.ts`) invokes this once, right after committing to a
 * `match`, for every kind of replaceable event.
 *
 * A no-op unless the ability itself asked for it: an ordinary continuous replacement keeps
 * matching every event for its whole duration, exactly as before. Also a no-op when there is no
 * `sourceModifier` — an authored replacement, or one a continuous static re-derives on every
 * read — because there is no record to mark (see `ReplacementAbility.consumesOnApply`).
 */
export function consumeIfOneShot(match: ReplacementMatch): void {
  if (match.ability.consumesOnApply !== true) return;
  const modifier = match.sourceModifier;
  if (modifier === null) return;
  if (!modifier.spentFor.includes(match.source)) modifier.spentFor.push(match.source);
}

/**
 * Cross a `limit: 'once-per-turn'` replacement off the moment it actually **applies** — same
 * "applied, not merely matched" discipline as `consumeIfOneShot`, so an event a different
 * replacement redirects, or one that never reaches this card at all, never spends the turn's
 * use. Tracked in `state.abilityUses` (cleared every Ready step, CR §6.1.13.2-shaped) rather
 * than a granted modifier's `spentFor`, because an intrinsic (printed) replacement — this is
 * the only kind `limit` is meaningful on — has no modifier record to mark. The caller
 * (`damage.ts`) invokes this alongside `consumeIfOneShot`, at both places a replacement match
 * is actually taken.
 */
export function consumeIfLimited(state: GameState, match: ReplacementMatch): void {
  if (match.ability.when.limit !== 'once-per-turn') return;
  const key = replacementLimitKey(match);
  if (!state.abilityUses.includes(key)) state.abilityUses.push(key);
}

// ---------------------------------------------------------------------------
// Rewriting
// ---------------------------------------------------------------------------

/**
 * What the caught event becomes. An empty result is the complete replacement — the event is
 * prevented and nothing happens at all. Every event returned re-enters the pipeline.
 *
 * Almost always damage events: `EventTemplate`'s `damage` case is how a caught lore loss or
 * discard can be prevented outright or turned *into* damage. The one exception is `ink`, whose
 * template is not a different kind at all — it re-raises the **same** caught `InkPendingEvent`
 * with `exerted` overridden, which is why it needs `event.kind === 'ink'` rather than a `CardRef`
 * to resolve: there is nothing to target, only a flag to flip.
 */
export function rewriteEvent(
  state: GameState,
  event: PendingEvent,
  match: ReplacementMatch,
): PendingEvent[] {
  const ctx = contextOf(match, event);
  const produced: PendingEvent[] = [];
  for (const template of match.ability.into) {
    if (template.kind === 'ink') {
      // Only an `ink` replacement's own templates are ever `ink` (`checkReplacement` in
      // dsl.ts enforces this at load time), so the caught event is always an ink event here.
      if (event.kind !== 'ink') continue;
      produced.push({ ...event, exerted: template.exerted });
      continue;
    }
    if (template.kind === 'to-inkwell') {
      // Only a `banish` replacement's own templates are ever `to-inkwell` (`checkReplacement`
      // enforces this at load time), so the caught event is always a banish event here. There
      // is nothing left to banish — it enters the replacement's own controller's inkwell
      // ("your inkwell"), the same card the caught event named.
      if (event.kind !== 'banish') continue;
      produced.push({
        kind: 'to-inkwell',
        player: match.controller,
        card: event.target,
        defId: instanceAt(state, event.target).defId,
        exerted: template.exerted ?? true,
        facedown: template.facedown ?? false,
        source: event.source,
      });
      continue;
    }
    const target = resolveCardRef(ctx, template.target);
    // "Do as much as you can" (CR §1.2.3): a template naming a card that is not there is
    // skipped, not an error.
    if (target === null) continue;
    produced.push({
      kind: 'damage',
      verb: template.verb,
      target,
      from: refOrNull(ctx, template.from),
      amount: evalEventAmount(state, ctx, event, template.amount),
      // The damage still comes from whatever was dealing it; a replacement changes what
      // happens, not who is doing it.
      source: event.source,
    });
  }
  return produced;
}

function refOrNull(ctx: AbilityContext, ref: CardRef | undefined): InstanceId | null {
  return ref === undefined ? null : resolveCardRef(ctx, ref);
}

/** The pending events that carry a natural "how many" — everything but one-card-at-a-time events. */
type QuantifiedPendingEvent =
  | DamagePendingEvent
  | LoseLorePendingEvent
  | GainLorePendingEvent
  | DiscardPendingEvent;

/**
 * Event kinds with no natural quantity — one card, one banish, never "how many". Exported for
 * `damage.ts`'s "nothing to do" check, which every replaceable event kind shares.
 */
export function hasAmount(event: PendingEvent): event is QuantifiedPendingEvent {
  return event.kind !== 'ink' && event.kind !== 'banish' && event.kind !== 'to-inkwell';
}

/**
 * Evaluate a replacement's amount, where `event-amount` reads the caught event's own. An `ink`,
 * `banish` or `to-inkwell` event has no natural amount — each is always exactly one card — so
 * `event-amount` reads as 0 for them; no printed card asks for this today, but the shape stays
 * total rather than partial.
 */
export function evalEventAmount(
  state: GameState,
  ctx: AbilityContext,
  event: PendingEvent,
  amount: EventAmount,
): number {
  if (typeof amount === 'number') return amount;
  if (amount.kind === 'event-amount') {
    const base = hasAmount(event) ? event.amount : 0;
    return Math.max(0, base + (amount.plus ?? 0));
  }
  return evalAmount(state, ctx, amount);
}
